import { DOCUMENT } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  APP_INITIALIZER,
  EnvironmentProviders,
  Optional,
  PLATFORM_ID,
  Provider,
  importProvidersFrom,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import {
  AUTH_CONFIG,
  AUTH_FEATURE_KEY,
  RECAPTCHA_OVERRIDE_TOKEN,
  provideAuth,
  recaptchaTokenInterceptor,
} from '@domgen/dgx-fe-authentication';
import { BASKET_CONFIG, provideBasket } from '@domgen/dgx-fe-basket';
import {
  CATALOGUE_CONFIG,
  CATALOGUE_FEATURE_KEY,
  provideCatalogue,
} from '@domgen/dgx-fe-catalogue';
import {
  CMS_CONFIG,
  CMS_CONTEXT,
  CMS_FEATURE_KEY,
  CmsConfig,
  cmsContextFactory,
  isInCmsEditorFactory,
  provideCms,
} from '@domgen/dgx-fe-cms';
import {
  BYPASS_ROUTE_GUARD,
  COMMON_CONFIG,
  COMMON_FEATURE_KEY,
  DOMAIN_ID,
  cookieProInitializer,
  correlationIdInterceptor,
  formatPayloadInterceptor,
  provideCommonStore,
  requestActionInterceptor,
  requestSourceInterceptor,
  sessionStorageSync,
  whiteLabelClientInterceptor,
} from '@domgen/dgx-fe-common';
import { CONFIG, ENVIRONMENT, provideConfig } from '@domgen/dgx-fe-config';
import { provideFormsAddressDefaults } from '@domgen/dgx-fe-forms/address';
import { FORM_CONFIG } from '@domgen/dgx-fe-forms/shared';
import {
  GA_CONFIG,
  GA_GLOBAL_DATA,
  gaHttpErrorInterceptor,
  provideGa,
} from '@domgen/dgx-fe-ga';
import { CountryIsoNumber, provideI18nDefaults } from '@domgen/dgx-fe-i18n';
import {
  LIGHTSTEP_CONFIG,
  provideLightstepInitializer,
} from '@domgen/dgx-fe-lightstep';
import {
  LoggingLevel,
  loggingInterceptor,
  provideLogging,
} from '@domgen/dgx-fe-logging';
import { provideNotificationsStore } from '@domgen/dgx-fe-notifications';
import { SEO_CONFIG, provideSeo } from '@domgen/dgx-fe-seo';
import { EditorContextService } from '@magnolia/angular-editor';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideRegistration } from '@us-registration/data-access';
import { REGISTRATION_CONFIG } from '@us-registration/util-models';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { environment } from '../environments/environment';
import { checkoutTokenInterceptor } from '../interceptors/checkout-token/checkout-token.interceptor';
import { addDateInterceptor } from '../interceptors/date-interceptor/add-date.interceptor';
import { getCheckoutTokenInterceptor } from '../interceptors/get-checkout-token/get-checkout-token.interceptor';
import { appRoutes } from './app.routes';
import { authConfigFactory } from './factories/auth-config/auth-config.factory';
import { basketConfigFactory } from './factories/basket-config/basket-config.factory';
import { catalogueConfigFactory } from './factories/catalogue-config/catalogue-config.factory';
import { commonConfigFactory } from './factories/common-config/common-config.factory';
import { domainIdFactory } from './factories/domain-id/domain-id.factory';
import { formConfigFactory } from './factories/form-config/form-config.factory';
import { gaConfigFactory } from './factories/ga-config/ga-config.factory';
import { gaGlobalDataFactory } from './factories/ga-global-data/ga-global-data.factory';
import { lightstepConfigFactory } from './factories/lightstep-config/lightstep-config.factory';
import { recaptchaOverrideTokenFactory } from './factories/recaptcha-override-token/recaptcha-override-token.factory';
import { recaptchaSiteKeyFactory } from './factories/recaptcha-site-key/recaptcha-site-key.factory';
import { registrationConfigFactory } from './factories/registration-config/registration-config.factory';
import { seoConfigFactory } from './factories/seo-config/seo-config.factory';

export function provideApp(): Provider | EnvironmentProviders[] {
  return [
    importProvidersFrom(RecaptchaV3Module),
    provideAnimations(),
    provideAuth(),
    provideBasket(),
    provideCatalogue(),
    provideCms(),
    provideCommonStore(),
    provideConfig(),
    provideEffects([]),
    provideFormsAddressDefaults(),
    provideGa(),
    provideI18nDefaults({ countryIsoNumber: CountryIsoNumber.UnitedStates }),
    provideLightstepInitializer(),
    provideLogging({
      level: LoggingLevel.Error,
      silent: environment.production,
    }),
    provideNotificationsStore(),
    provideRegistration(),
    provideRouter(
      appRoutes,
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
      })
    ),
    provideSeo(),
    provideStore(
      {},
      {
        metaReducers: [
          sessionStorageSync({
            keys: [
              AUTH_FEATURE_KEY,
              CATALOGUE_FEATURE_KEY,
              CMS_FEATURE_KEY,
              COMMON_FEATURE_KEY,
            ],
            rehydrate: true,
          }),
        ],
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
        },
      }
    ),
    !environment.production ? provideStoreDevtools() : [],
    {
      provide: APP_INITIALIZER,
      useFactory: cookieProInitializer,
      multi: true,
      deps: [
        DOCUMENT,
        COMMON_CONFIG,
        PLATFORM_ID,
        [new Optional(), BYPASS_ROUTE_GUARD],
      ],
    },
    {
      provide: AUTH_CONFIG,
      useFactory: authConfigFactory,
      deps: [CONFIG],
    },
    {
      provide: BYPASS_ROUTE_GUARD,
      useFactory: isInCmsEditorFactory,
      deps: [CMS_CONTEXT],
    },
    {
      provide: BASKET_CONFIG,
      useFactory: basketConfigFactory,
      deps: [CONFIG],
    },
    {
      provide: CATALOGUE_CONFIG,
      useFactory: catalogueConfigFactory,
      deps: [CONFIG],
    },

    {
      provide: COMMON_CONFIG,
      useFactory: commonConfigFactory,
      deps: [CONFIG],
    },
    {
      provide: CMS_CONTEXT,
      useFactory: (
        cmsConfig: CmsConfig,
        editorContextService: EditorContextService
      ) => cmsContextFactory()(cmsConfig.cmsRootPath, editorContextService),
      deps: [CMS_CONFIG, EditorContextService],
    },
    {
      provide: DOMAIN_ID,
      useFactory: domainIdFactory,
      deps: [CONFIG],
    },
    {
      provide: ENVIRONMENT,
      useValue: environment,
    },
    {
      provide: FORM_CONFIG,
      useFactory: formConfigFactory,
      deps: [CONFIG],
    },
    {
      provide: GA_CONFIG,
      useFactory: gaConfigFactory,
      deps: [CONFIG],
    },
    {
      provide: GA_GLOBAL_DATA,
      useFactory: gaGlobalDataFactory,
      deps: [CONFIG],
    },
    provideHttpClient(
      withInterceptors([
        correlationIdInterceptor,
        formatPayloadInterceptor,
        gaHttpErrorInterceptor,
        loggingInterceptor,
        recaptchaTokenInterceptor,
        requestActionInterceptor,
        requestSourceInterceptor,
        getCheckoutTokenInterceptor,
        checkoutTokenInterceptor,
        whiteLabelClientInterceptor,
        addDateInterceptor,
      ])
    ),
    {
      provide: LIGHTSTEP_CONFIG,
      useFactory: lightstepConfigFactory,
      deps: [CONFIG],
    },
    {
      provide: RECAPTCHA_OVERRIDE_TOKEN,
      useFactory: recaptchaOverrideTokenFactory,
      deps: [CONFIG],
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useFactory: recaptchaSiteKeyFactory,
      deps: [CONFIG],
    },
    {
      provide: REGISTRATION_CONFIG,
      useFactory: registrationConfigFactory,
      deps: [CONFIG],
    },
    {
      provide: SEO_CONFIG,
      useFactory: seoConfigFactory,
      deps: [CONFIG],
    },
  ];
}
